import { Container, Graphics, LINE_CAP, Text } from "pixi.js"
import { TEXT_ROLES } from "../text/typography"
import { TextRenderer } from "../text/text_renderer"
import { GOLD, MUTED, RIM, RIM_LIGHT, TIMBER } from "./build_board"
import { interpolateColor } from "./dial"

// THE EMBER GAUGE: how the camp's fire is doing, continuously, in the slot under the countdown dial.
//
// The game is named after this fire and until now you could not see its state at all. The only thing
// that ever mentioned it was the lord's alarm pill (ember_roster.ts), and that does not appear until
// `fireStrength` has already fallen below 0.3 -- the point at which the fire has started draining the
// lord's health. So the one resource the whole loop is built on was invisible right up to the moment
// it was already costing you, and "go and fetch wood" could only ever be a reaction.
//
// This is the ambient reading; the roster's pill stays the ACUTE alarm. That division is the row's
// own rule (roster_status.ts: only an alarm is filled) applied one level up -- a quiet gauge you can
// watch, and a loud pill when it is too late to keep watching.
//
// THE BANDS ARE THE SERVER'S, and they are marked on the groove rather than merely coloured, because
// they are a mechanic the player is otherwise never told: above 0.7 the fire heals the lord, below
// 0.3 it drains him, and between the two it does nothing (campaigns/ember, and the client's own
// ember_lord.test.ts documents all three). Marking them means a player can watch the fill approach a
// line and go for wood BEFORE crossing it, which is the whole difference between a resource and a
// warning light.
export const FIRE_DRAIN_BELOW = 0.3
export const FIRE_HEAL_ABOVE = 0.7

const PLATE_H = 22
const PLATE_RADIUS = 5
const PLATE_PAD_X = 7
const SHADOW_DROP = 1

const GROOVE_H = 7
const GROOVE_RADIUS = 3.5

// Room kept at the right for the band word. Measured, not guessed: "LOW" and "OUT" are 22 and 20
// wide at this size, and the space is RESERVED even while empty so the bar is the same length in
// every state -- a gauge whose bar grows as the fire improves would be reading two things at once.
const LABEL_RESERVE = 28
// Light enough to read off the timber plate. The bar carries the dying colour; the word only has to
// be legible, and a word tinted the same near-black as an empty fire cannot be read at all.
const LABEL_COLOR = 0xff9d8a

// Ember gold while it is healing, amber while it is merely holding, and a dying red below the drain
// line. Green-free, like the countdown dial's: this is a fire, and a fire has no green in it.
const STRONG_COLOR = GOLD
const STEADY_COLOR = 0xff9d2e
const LOW_COLOR = 0xe8542d
const OUT_COLOR = 0x8c2f1d

// How long the bar takes to reach a new reading -- a DURATION, not a rate. A rate (full sweep in
// 420ms) made every small change finish in under a tenth of a second, so the one thing the fire
// actually does -- tick quietly down -- snapped instead of moving. Each reading now takes the same
// beat to arrive however far it has to go, which is what makes a log landing something you SEE.
const FILL_MS = 520

// The flame's own clock. It burns whatever the fire is doing: a gauge whose only moving part moves
// solely when you are already in trouble is a gauge nobody looks at the rest of the time.
//
// SLOW, and much slower than the first attempt. At 190ms and 437ms (5Hz against 2Hz) the glyph
// vibrated -- it read as a fault in the UI rather than as a flame. A real flame DRIFTS, with the
// occasional lick: the periods below are near two seconds, far apart and in no simple ratio, so the
// two never line up and the loop never becomes visible. Amplitudes are under a pixel on purpose;
// this is a 10px glyph and anything you can measure by eye on it is a twitch.
const FLICKER_DRIFT_MS = 2100
const FLICKER_LICK_MS = 1300
// The breath below the drain line. Slow -- this is a worry, not an alarm; the roster has the alarm.
const BREATH_MS = 1400

export type FireBand = "out" | "low" | "steady" | "strong"

/** Which of the server's three cold-health bands this strength is in. */
export const fireBand = (strength: number): FireBand => {
    if (!(strength > 0)) {
        return "out"
    }
    if (strength < FIRE_DRAIN_BELOW) {
        return "low"
    }
    return strength < FIRE_HEAL_ABOVE ? "steady" : "strong"
}

/**
 * The word on the plate -- and there is one only when something is WRONG.
 *
 * "STRONG" and "STEADY" measure 39 and 37 against a 118px plate, so naming the healthy states cost a
 * third of the row to say what the fill and its colour already said. A word that appears is also a
 * far stronger signal than a word that changes: the same bargain the roster's pill row strikes one
 * level down (roster_status.ts -- only an alarm is filled). The bar keeps the reserved space either
 * way, so it never changes length under the reading it is showing.
 */
export const fireBandLabel = (band: FireBand): string =>
    band === "out" ? "OUT" : band === "low" ? "LOW" : ""

/**
 * The bar's colour at a given strength. Continuous, so the fill darkens as it falls rather than
 * flicking between three colours -- the BAND is what steps, and the label is what says so.
 */
export const fireColor = (strength: number): number => {
    if (!(strength > 0)) {
        return OUT_COLOR
    }
    if (strength < FIRE_DRAIN_BELOW) {
        return interpolateColor(OUT_COLOR, LOW_COLOR, strength / FIRE_DRAIN_BELOW)
    }
    if (strength < FIRE_HEAL_ABOVE) {
        return interpolateColor(LOW_COLOR, STEADY_COLOR, (strength - FIRE_DRAIN_BELOW) / (FIRE_HEAL_ABOVE - FIRE_DRAIN_BELOW))
    }
    return interpolateColor(STEADY_COLOR, STRONG_COLOR, (strength - FIRE_HEAL_ABOVE) / (1 - FIRE_HEAL_ABOVE))
}

export class EmberFireGauge extends Container {
    private plate = new Graphics()
    private bar = new Graphics()
    private flame = new Graphics()
    private bandText: Text

    private width_ = 118
    // The bar's travel: where it set off from, where it is going, and how far through it is.
    private shown = 0
    private from = 0
    private target = 0
    private travelMs = FILL_MS
    private strength = 0
    private breathMs = 0
    private flickerMs = 0
    private needsDraw = true

    constructor() {
        super()
        this.bandText = new TextRenderer()
            .color(MUTED)
            .fontSize(TEXT_ROLES.overline.fontSize)
            .bold()
            .align("right")
            .render("") as Text
        this.bandText.anchor.set(1, 0.5)
        this.addChild(this.plate, this.bar, this.flame, this.bandText)
        this.visible = false
    }

    /** Match the dial above, so the top-left column reads as one stack rather than two widgets. */
    public setWidth = (width: number) => {
        if (this.width_ === width) {
            return
        }
        this.width_ = width
        this.needsDraw = true
    }

    public get height_(): number {
        return PLATE_H
    }

    /**
     * The camp's fire, 0..1, or undefined when there is no camp to report one (every room that is
     * not an ember camp, and any roster packet that predates the field).
     */
    public update = (strength?: number) => {
        const known = Number.isFinite(strength as number)
        if (!known) {
            if (this.visible) {
                this.visible = false
            }
            return
        }
        const next = Math.max(0, Math.min(1, strength as number))
        this.visible = true
        if (next === this.strength) {
            return
        }
        this.strength = next
        // Set off from wherever the bar actually IS, not from the last reading: a second change
        // arriving mid-travel would otherwise jump back to the start of the first one.
        this.from = this.shown
        this.target = next
        this.travelMs = 0
        this.needsDraw = true
    }

    /** Advance one frame. Does nothing on a gauge that is settled and out of the worry band. */
    public animate = (deltaMs: number) => {
        if (!this.visible) {
            return
        }
        // The flame burns every frame, at every strength. It repaints ONLY itself (see drawFlame),
        // so the plate and the bar underneath are still only redrawn when they actually change.
        this.flickerMs += deltaMs
        this.drawFlame()

        let moving = false
        if (this.travelMs < FILL_MS) {
            this.travelMs = Math.min(FILL_MS, this.travelMs + deltaMs)
            // Eased over a fixed duration, and LANDED exactly at the end rather than approached --
            // a bar forever 2% short is the same lie the hearts would have told (gui/icon_meter.ts).
            const t = this.travelMs / FILL_MS
            this.shown = this.travelMs >= FILL_MS
                ? this.target
                : this.from + (this.target - this.from) * (1 - Math.pow(1 - t, 3))
            moving = true
        }
        if (this.strength < FIRE_DRAIN_BELOW) {
            this.breathMs = (this.breathMs + deltaMs) % BREATH_MS
            moving = true
        } else if (this.breathMs !== 0) {
            this.breathMs = 0
            moving = true
        }
        if (!moving && !this.needsDraw) {
            return
        }
        this.needsDraw = false
        this.redraw()
    }

    /** Test seam: the fill the bar is SHOWING, which lags the reading while it travels. */
    public shownFill = (): number => this.shown

    private redraw = () => {
        const band = fireBand(this.strength)
        const color = fireColor(this.strength)
        // Below the drain line the whole plate breathes. Above it, nothing moves.
        const breath = band === "low" || band === "out"
            ? 0.5 + 0.5 * Math.sin((this.breathMs / BREATH_MS) * Math.PI * 2)
            : 0

        this.drawPlate()
        this.drawBar(color, breath)

        const label = fireBandLabel(band)
        this.bandText.visible = label.length > 0
        if (this.bandText.text !== label) {
            this.bandText.text = label
        }
        this.bandText.tint = LABEL_COLOR
        this.bandText.position.set(this.width_ - PLATE_PAD_X, 0)
    }

    private drawPlate = () => {
        const top = -PLATE_H / 2
        this.plate.clear()
        this.plate.beginFill(0x000000, 0.34)
        this.plate.drawRoundedRect(0, top + SHADOW_DROP, this.width_, PLATE_H, PLATE_RADIUS)
        this.plate.endFill()
        this.plate.lineStyle({ width: 1, color: RIM, alpha: 0.45 })
        this.plate.beginFill(TIMBER, 0.96)
        this.plate.drawRoundedRect(0, top, this.width_, PLATE_H, PLATE_RADIUS)
        this.plate.endFill()
        this.plate.lineStyle(0)
    }

    /**
     * The flame on the left, which DANCES -- every frame, at every strength.
     *
     * It is the plate's one moving part and the only thing on it that says "fire" without spending
     * any of a 118px row on the word. A still flame reads as an icon; a moving one reads as the
     * thing itself, and it is what makes the gauge worth glancing at while the fire is fine.
     *
     * Two frequencies that do not divide into each other drive three separate things -- height, the
     * lean of the tip, and the hot heart -- so no two of them peak together and the loop never
     * becomes visible. The amplitude rides on STRENGTH: a strong fire leaps, a dying one gutters,
     * which is a second reading of the gauge for anyone not looking directly at the bar.
     *
     * Repaints only its own Graphics, so running every frame leaves the plate and bar alone.
     */
    private drawFlame = () => {
        const drift = Math.sin((this.flickerMs / FLICKER_DRIFT_MS) * Math.PI * 2)
        const lick = Math.sin((this.flickerMs / FLICKER_LICK_MS) * Math.PI * 2)
        const life = 0.4 + 0.6 * this.shown

        const x = PLATE_PAD_X + 3
        const h = 10.5 + (drift * 0.5 + lick * 0.35) * life
        // The tip leans; the base does not. A flame pinned at both ends is a leaf.
        const lean = (drift * 0.75 + lick * 0.3) * life
        const waist = 3 + drift * 0.2 * life
        const color = fireColor(this.strength)

        this.flame.clear()
        this.flame.beginFill(color, 0.92)
        this.flame.moveTo(x + lean, -h / 2)
        this.flame.quadraticCurveTo(x + 4.5, -h / 6, x + waist, h / 4)
        this.flame.quadraticCurveTo(x + 1.5, h / 2, x, h / 2)
        this.flame.quadraticCurveTo(x - 1.5, h / 2, x - waist, h / 4)
        this.flame.quadraticCurveTo(x - 4.5, -h / 6, x + lean, -h / 2)
        this.flame.endFill()
        // The hot heart, so the glyph does not read as a flat leaf. Opaque and small, under a
        // translucent body -- the fire's own rule (see additive-blending notes on the camp fire).
        this.flame.beginFill(0xfff2d0, 0.8)
        this.flame.drawEllipse(x, h / 5, 1.2 + lick * 0.1 * life, 1.9 + drift * 0.2 * life)
        this.flame.endFill()
    }

    private drawBar = (color: number, breath: number) => {
        const left = PLATE_PAD_X + 11
        // Leave the band word its room on the right, whether or not there is a word today.
        const right = this.width_ - PLATE_PAD_X - LABEL_RESERVE
        const span = Math.max(8, right - left)
        const top = -GROOVE_H / 2

        this.bar.clear()
        this.bar.beginFill(0x000000, 0.55)
        this.bar.drawRoundedRect(left, top, span, GROOVE_H, GROOVE_RADIUS)
        this.bar.endFill()

        if (this.shown > 0) {
            const fill = Math.max(GROOVE_H, span * this.shown)
            this.bar.beginFill(color, 0.35 + 0.25 * breath)
            this.bar.drawRoundedRect(left - 1, top - 1, fill + 2, GROOVE_H + 2, GROOVE_RADIUS + 1)
            this.bar.endFill()
            this.bar.beginFill(color, 1)
            this.bar.drawRoundedRect(left, top, fill, GROOVE_H, GROOVE_RADIUS)
            this.bar.endFill()
        }

        // The two lines the server actually cares about, cut through the groove. Faint: they are a
        // reference the eye finds when it looks for them, not furniture competing with the fill.
        for (const at of [FIRE_DRAIN_BELOW, FIRE_HEAL_ABOVE]) {
            const x = Math.round(left + span * at) + 0.5
            this.bar.lineStyle({ width: 1, color: RIM_LIGHT, alpha: 0.5, cap: LINE_CAP.SQUARE })
            this.bar.moveTo(x, top - 1.5)
            this.bar.lineTo(x, top + GROOVE_H + 1.5)
        }
        this.bar.lineStyle(0)
    }
}
